import { CertificationType } from "@/models/Organisation";
import { useOrganisation, useUpdateOrganisation } from "@/networking/organisation.hooks";
import { slugifyKey } from "@/utils/string.utils";
import { SettingsLeaf } from "./SettingsLeafLayout";
import TaxonomyCrudEditor, { TaxonomyItemChanges } from "./TaxonomyCrudEditor";

/**
 * "Certifikat" — CRUD over `organisation.certificationTypes`. Unlike the tag
 * taxonomies (dedicated endpoints), certification types are saved via the
 * existing organisation update PUT as a whole-array replace, so keys are
 * generated client-side (auto-slug from the Swedish name).
 */
const CertifikatPage = () => {
	const { data: organisation } = useOrganisation();
	const { mutateAsync: updateOrganisation, isLoading: isSaving } = useUpdateOrganisation();

	const certificationTypes = organisation?.certificationTypes ?? [];

	const save = (next: CertificationType[]) => updateOrganisation({ certificationTypes: next });

	const uniqueKey = (name: string) => {
		const base = slugifyKey(name) || "certifikat";
		const existing = new Set(certificationTypes.map((c) => c.key));
		let key = base;
		let suffix = 2;
		while (existing.has(key)) key = `${base}-${suffix++}`;
		return key;
	};

	// No org-level validity here — a certificate's expiry date is set per
	// consultant when it is assigned. Validity durations exist only on tags.
	const toCertificationType = (key: string, changes: TaxonomyItemChanges): CertificationType => ({
		key,
		name: changes.name,
		...(changes.requiresProof ? { requiresProof: true } : {}),
		...(changes.hasExpirationDate ? { hasExpirationDate: true } : {}),
		...(changes.visibleToConsultant ? { visibleToConsultant: true } : {}),
		...(changes.showInListViews ? { showInListViews: true } : {}),
		...(changes.remindBeforeExpiry
			? {
					remindBeforeExpiry: true,
					reminderDaysBeforeExpiry: changes.reminderDaysBeforeExpiry,
			  }
			: {}),
	});

	return (
		<SettingsLeaf
			title="Certifikat"
			description="Certifikat som kan tilldelas konsulter (t.ex. polisregisterutdrag eller körkort)."
		>
			<TaxonomyCrudEditor
				items={certificationTypes}
				addLabel="Lägg till certifikat"
				emptyText="Inga certifikat har skapats ännu."
				showRequiresProof
				showHasExpiration
				showVisibleToConsultant
				showListViewVisibility
				showReminder
				badgeVariant="credential"
				isSaving={isSaving}
				onCreate={(changes) =>
					save([...certificationTypes, toCertificationType(uniqueKey(changes.name.sv), changes)])
				}
				onUpdate={(key, changes) =>
					save(
						certificationTypes.map((c) => (c.key === key ? toCertificationType(key, changes) : c))
					)
				}
				onDelete={(key) => save(certificationTypes.filter((c) => c.key !== key))}
				deleteTitle={(item) => `Ta bort certifikatet "${item.name.sv}"?`}
				deleteDescription={() =>
					"Certifikatet kan inte längre tilldelas konsulter eller användas som krav. Detta går inte att ångra."
				}
			/>
		</SettingsLeaf>
	);
};

export default CertifikatPage;
